import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ImagePlaceholder,
  PrimaryButton,
  SecondaryButton,
  OptionCard,
  IconGlyph,
} from '../ui/Primitives.jsx';
import { resolveImage, cmToFtIn, kgToLb, fillTemplate, formatDatePlusDays } from '../../lib/helpers.js';
import { computeIMC, computeProfileSummary, computeConsistencyProjection } from '../../lib/fitnessScore.js';
import { t, currentLanguage } from '../../i18n/strings.js';
import { quizSteps } from '../../config/quizConfig.js';
import { saveLead } from '../../lib/leads.js';
import { PRIVACY_URL, TERMS_URL } from '../../config/storeLinks.js';

// ============================================================================
// StepRenderer — desenha CADA tipo de etapa do quiz a partir do objeto de
// configuração (fitclutch_quiz_config.js). Um switch por "type", cada um sabe
// se avança sozinho (single-select) ou espera um botão "Continuar".
// ============================================================================
export default function StepRenderer({ step, gender, answers, value, onAnswer, onNext, onBack }) {
  switch (step.type) {
    case 'intro':
      return <IntroStep step={step} gender={gender} onNext={onNext} />;
    case 'age-select':
      return <AgeSelectStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'gender-select':
      return <GenderSelectStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'social-proof':
      return <SocialProofStep step={step} answers={answers} onNext={onNext} />;
    case 'goal-select':
      return <GoalSelectStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'body-shape-select':
      return <BodyShapeStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} answers={answers} />;
    case 'body-goal-select':
      return <BodyGoalStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} answers={answers} />;
    case 'last-best-select':
      return <LastBestStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'weight-trend-select':
      return <WeightTrendStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'push-ups-select':
      return <PushUpsStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'squats-select':
      return <SquatsStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'flexibility-select':
      return <FlexibilityStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'progress-promo':
      return <ProgressPromoStep step={step} onNext={onNext} />;
    case 'transform-promo':
      return <TransformPromoStep step={step} answers={answers} onNext={onNext} />;
    case 'training-summary-promo':
      return <TrainingSummaryPromoStep step={step} answers={answers} onNext={onNext} />;
    case 'options-icon':
      return <OptionsStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} withImages={false} />;
    case 'options-image':
      return <OptionsStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} withImages gender={gender} />;
    case 'multi-select-grid':
      return <MultiSelectStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} gender={gender} />;
    case 'water-intake-select':
      return <WaterIntakeStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'profile-summary':
      return <ProfileSummaryStep step={step} answers={answers} gender={gender} onNext={onNext} />;
    case 'consistency-chart':
      return <ConsistencyChartStep step={step} answers={answers} onNext={onNext} />;
    case 'progress-loader':
      return <ProgressLoaderStep step={step} onNext={onNext} />;
    case 'email-capture':
      return <EmailCaptureStep step={step} value={value} answers={answers} onAnswer={onAnswer} onNext={onNext} />;
    case 'discount-reveal':
      return <DiscountRevealStep step={step} onNext={onNext} />;
    case 'body-map-list':
      return <BodyMapStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} gender={gender} />;
    case 'target-zone-select':
      return <TargetZoneStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} answers={answers} />;
    case 'wheel-picker':
      return <WheelPickerStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'ruler-slider':
      return <RulerSliderStep step={step} value={value} onAnswer={onAnswer} onNext={onNext} />;
    case 'ruler-slider-feedback':
      return <RulerSliderFeedbackStep step={step} value={value} answers={answers} onAnswer={onAnswer} onNext={onNext} />;
    case 'prediction-chart':
      return <PredictionChartStep step={step} answers={answers} onNext={onNext} />;
    case 'transformation-compare':
      return <TransformationStep step={step} gender={gender} onNext={onNext} />;
    case 'value-comparison':
      return <ValueComparisonStep step={step} onNext={onNext} />;
    case 'permission':
      return <PermissionStep step={step} answers={answers} onAnswer={onAnswer} onNext={onNext} />;
    case 'loading-analysis':
      return <LoadingAnalysisStep step={step} onNext={onNext} />;
    default:
      return (
        <div className="py-10 text-center text-ink-muted text-sm">
          Tipo de etapa desconhecido: {step.type}
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
function IntroStep({ step, gender, onNext }) {
  const bg = resolveImage(step.backgroundImage, gender);
  return (
    <div className="flex-1 flex flex-col justify-end min-h-[90vh] -mx-5">
      <div className="relative flex-1">
        <ImagePlaceholder src={bg} className="absolute inset-0 w-full h-full object-cover" label="Foto/vídeo de fundo" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/40 to-transparent" />
      </div>
      <div className="px-5 pb-8 pt-4">
        <h1 className="font-display text-3xl font-extrabold leading-tight mb-2">{step.title}</h1>
        <p className="text-ink-muted mb-6">{step.subtitle}</p>
        <PrimaryButton onClick={onNext}>{step.cta}</PrimaryButton>
        {step.secondaryAction && (
          <button className="w-full text-center text-sm text-ink-muted mt-4 underline">
            {step.secondaryAction.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AgeSelectStep — tela de entrada do funil (ver quizConfig.js, 'age_select').
// Não usa o phone-frame estreito do ScreenShell pra essa tela específica
// porque ela já é renderizada DENTRO do ScreenShell pelo QuizFlow; o que
// deixamos responsivo aqui é o GRID de cartões (2 colunas no celular, até 4
// numa tela maior) e o tamanho do texto, pra nunca sobrar/faltar espaço —
// pedido explícito da Gladis (01/10/2026) depois dos ajustes manuais de
// tamanho que tivemos que fazer no quiz antigo.
function AgeSelectStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(range) {
    setSelected(range.value);
    onAnswer(range);
    setTimeout(onNext, 180);
  }

  return (
    // h-[100dvh] + overflow-hidden: a tela INTEIRA (logo até o rodapé de
    // termos) é desenhada pra caber exatamente na altura real da janela do
    // navegador no celular (100dvh já desconta a barra do Safari/Chrome,
    // diferente de 100vh) — sem rolagem, sem "pular" quando a pessoa
    // arrasta o dedo. O grid de fotos usa flex-1 (cresce/encolhe pra
    // preencher o espaço que sobrar) em vez de uma altura fixa, então em
    // qualquer tamanho de tela (celular pequeno, grande, ou PC) tudo se
    // reparte sozinho — não precisa mais ajustar tamanho na mão depois
    // (pedido da Gladis, 01/10/2026).
    <div className="min-h-full flex flex-col items-center text-center w-full max-w-sm mx-auto px-1 pt-2 pb-3 sm:pt-6 sm:pb-6">
      <img
        src="/logo-fc.png"
        alt="FitClutch"
        className="w-10 h-10 sm:w-14 sm:h-14 mb-3 sm:mb-5 rounded-xl shrink-0"
      />

      <h1 className="font-display font-extrabold leading-tight text-xl sm:text-4xl md:text-5xl shrink-0">
        {t('ageSelect.titleLine1')}
      </h1>
      <h1 className="font-display font-extrabold leading-tight text-xl sm:text-4xl md:text-5xl text-cyan mt-0.5 sm:mt-1 shrink-0">
        {t('ageSelect.titleHighlight')}
      </h1>

      <p className="text-ink-muted mt-2 sm:mt-3 text-xs sm:text-base shrink-0">{t('ageSelect.poweredBy')}</p>

      <div className="flex items-center gap-2 mt-3 sm:mt-5 text-xs sm:text-base shrink-0">
        <span>{t('ageSelect.subtitle')}</span>
      </div>
      <div className="flex items-center gap-1.5 text-ink-muted text-[11px] sm:text-sm mt-0.5 shrink-0">
        <span>⏱</span>
        <span>{t('ageSelect.duration')}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4 mt-3 sm:mt-6 w-full shrink-0 overflow-hidden">
        {step.ageRanges.map((range) => (
          <button
            key={range.value}
            onClick={() => choose(range)}
            className={`group rounded-xl2 overflow-hidden border-2 transition-colors text-left flex flex-col ${
              selected === range.value ? 'border-cyan' : 'border-transparent'
            }`}
          >
            <AgeCardImage src={range.image} label={range.value} />
            <div className="bg-surface px-2.5 py-2 flex items-center justify-between shrink-0">
              <span className="font-display font-semibold text-xs sm:text-base">
                {t('ageSelect.ageLabel', { range: range.value })}
              </span>
              <span className="text-cyan">→</span>
            </div>
          </button>
        ))}
      </div>

      <p className="text-[10px] sm:text-xs text-ink-muted mt-2 sm:mt-6 max-w-md shrink-0 leading-snug">
        {fillTemplate(t('ageSelect.footnote'), {
          terms: `<terms>`,
          privacy: `<privacy>`,
        })
          .split(/(<terms>|<privacy>)/)
          .map((part, i) =>
            part === '<terms>' ? (
              <a key={i} href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline text-cyan font-semibold">
                {t('ageSelect.termsLink')}
              </a>
            ) : part === '<privacy>' ? (
              <a key={i} href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline text-cyan font-semibold">
                {t('ageSelect.privacyLink')}
              </a>
            ) : (
              part
            )
          )}
      </p>
    </div>
  );
}

// Imagem do cartão de faixa etária — SEM proporção fixa de propósito: usa
// flex-1 + object-cover pra sempre preencher a altura que sobrar dentro do
// cartão (que por sua vez vem do grid com flex-1 lá em cima). Isso é o que
// garante a tela inteira caber sem rolar em QUALQUER tamanho de celular,
// sem precisar recalcular nada na mão depois. Tem um fallback visual (sem
// quebrar o layout) caso o link do banco de imagens pare de funcionar — a
// Gladis troca pelas imagens definitivas depois. Reaproveitado também pela
// GenderSelectStep (é o mesmo tipo de cartão foto+label), apesar do nome.
// Proporção fixa (retrato, igual ao cartão do Befit) em vez de esticar pra
// preencher toda a altura que sobrar — pedido da Gladis (01/10/2026):
// "as imagens não precisam ocupar a tela inteira, está muito esticada,
// deixe como o Befit". Não precisa de scroll mesmo assim: a tela só fica
// com espaço vazio sobrando embaixo, igual ao print de referência.
function AgeCardImage({ src, label }) {
  const [failed, setFailed] = useState(false);
  // max-h junto com aspect-ratio: trava uma altura máxima (fração da tela)
  // pra imagem NUNCA empurrar o rodapé (termos/privacidade) pra fora da
  // área visível — foi o que sumiu com o rodapé quando deixamos as fotos
  // em proporção fixa sem limite de altura (bug visto pela Gladis em
  // 01/10/2026, corrigido aqui).
  if (failed || !src) {
    return (
      <div className="age-card-img w-full aspect-[3/4] max-h-[24vh] bg-surface-2 flex items-center justify-center text-ink-muted text-sm shrink-0">
        {label}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={label}
      onError={() => setFailed(true)}
      className="age-card-img w-full aspect-[3/4] max-h-[24vh] object-cover shrink-0"
    />
  );
}

// Fileira de 5 estrelinhas (nota das lojas) — dourado, convenção universal
// de avaliação, igual ao print de referência do Befit.
function StarRow() {
  return (
    <div className="flex items-center gap-0.5 text-amber-400 text-[10px] sm:text-xs leading-none">
      {'★★★★★'}
    </div>
  );
}

// Logo real da Apple (caminho vetorial oficial, biblioteca simple-icons —
// o desenho à mão anterior tinha ficado torto/malformado, a Gladis apontou
// o problema em 01/10/2026 e pediu pra usar algo já pronto e correto).
function AppStoreIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="11" fill="#0B84FF" />
      <g transform="translate(10,8) scale(1.15)" fill="#fff">
        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zm3.63-3.333c.843-1.012 1.4-2.427 1.245-3.843-1.207.052-2.662.805-3.531 1.817-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.688z" />
      </g>
    </svg>
  );
}

// Logo real do Google Play (triângulo de 4 cores, oficial, sobre o mesmo
// fundo branco arredondado que o selo real usa — igual ao padrão da Apple
// acima, pra ficar consistente entre os dois).
// Triângulo do Google Play construído por geometria exata (não mais um
// desenho à mão livre) — o mesmo ponto-pivô do lado esquerdo se liga a dois
// pontos próximos da ponta, abrindo as 4 cores reais (azul, verde, amarelo,
// vermelho) sem nenhuma folga/rasgo entre elas. Conferido com captura de
// tela antes de entregar (01/10/2026), depois da versão anterior sair
// torta.
function GooglePlayIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="11" fill="#fff" />
      <polygon points="14,11 14,24 36.3,22.3" fill="#00D2FF" />
      <polygon points="36.3,22.3 14,24 38.5,24" fill="#34D399" />
      <polygon points="14,37 14,24 36.3,25.7" fill="#FF5C5C" />
      <polygon points="36.3,25.7 14,24 38.5,24" fill="#FFCA28" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// GenderSelectStep — 2 fotos grandes lado a lado (estrutura da tela "What
// is your gender" do Befit), com borda ciano no selecionado. Segue o mesmo
// padrão de altura travada (h-full + overflow-hidden, sem rolar) que já
// usamos na tela de idade — aqui o cabeçalho (voltar + logo + barra de
// progresso) já vem de fora, do ScreenShell, então só precisamos encaixar
// nossa altura DENTRO do espaço que sobra.
function GenderSelectStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);
  const options = [
    { value: 'male', label: t('gender.male'), image: step.images?.male },
    { value: 'female', label: t('gender.female'), image: step.images?.female },
  ];

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('gender.title')}
      </h2>
      <p className="text-ink-muted text-sm sm:text-base mt-1.5 shrink-0">{t('gender.subtitle')}</p>

      <div className="grid grid-cols-2 gap-3 sm:gap-5 mt-4 shrink-0 overflow-hidden">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => choose(opt.value)}
            className={`rounded-xl2 overflow-hidden border-2 transition-colors flex flex-col ${
              selected === opt.value ? 'border-cyan' : 'border-transparent'
            }`}
          >
            <AgeCardImage src={opt.image} label={opt.label} />
            <div className="bg-surface px-3 py-2.5 flex items-center justify-center shrink-0">
              <span className="font-display font-semibold text-sm sm:text-lg">{opt.label}</span>
            </div>
          </button>
        ))}
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// SocialProofStep — estrutura da tela "Over 300,000 women..." do Befit:
// título grande + frase de apoio com trecho destacado + foto + selo de
// avaliação das lojas (com coroa de louro + ícones + estrelas, igual ao
// print de referência) + CTA. Tela transitória, sem barra de progresso
// (igual 'transformation'/'weight_prediction'), mesmo padrão de altura
// mínima das demais (nunca corta o rodapé/CTA — bug corrigido em
// 01/10/2026).
// Reescrita em 01/10/2026 (pedido da Gladis: "deixe igual [ao Befit],
// mudando algumas palavras ou melhor, nunca pior") — e corrigido o bug do
// placeholder "{users}" aparecendo literal na tela (faltava passar a
// variável pro t()).
function SocialProofStep({ step, answers, onNext }) {
  const genderAnswer = answers?.gender; // 'male' | 'female' | 'other' | undefined
  const genderWord =
    genderAnswer === 'male'
      ? t('socialProof.genderMen')
      : genderAnswer === 'female'
      ? t('socialProof.genderWomen')
      : t('socialProof.genderPeople');

  const ageRange = answers?.age_select; // { value, min, max, image }
  const decade = ageRange ? Math.floor(ageRange.min / 10) * 10 : null;
  const decadeWord = decade ? t(`socialProof.decade${decade}`) : '';
  const users = step.stats?.users ?? '';

  const proofImage =
    step.images?.[genderAnswer] ?? step.images?.other ?? step.image ?? null;

  const headlineBig = t('socialProof.headlineBig', { users, gender: genderWord });
  const subcopyRaw = decade
    ? t('socialProof.subcopy', { decade: decadeWord })
    : t('socialProof.subcopyNoDecade');

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {headlineBig}
      </h2>

      <p className="text-ink-muted text-sm sm:text-base leading-snug mt-2 shrink-0">
        {subcopyRaw.split(/(<hl>|<\/hl>)/).reduce(
          (acc, part) => {
            if (part === '<hl>') return { ...acc, open: true };
            if (part === '</hl>') return { ...acc, open: false };
            acc.nodes.push(
              acc.open ? (
                <span key={acc.nodes.length} className="text-cyan font-semibold">
                  {part}
                </span>
              ) : (
                part
              )
            );
            return acc;
          },
          { open: false, nodes: [] }
        ).nodes}
      </p>

      <div className="w-full shrink-0 rounded-xl2 overflow-hidden mt-4">
        {proofImage ? (
          <img src={proofImage} alt="FitClutch" className="block w-full h-auto" />
        ) : (
          <AgeCardImage src={proofImage} label="FitClutch" />
        )}
      </div>

      <div className="flex items-center justify-center gap-4 sm:gap-7 mt-5 shrink-0">
        <div className="flex flex-col items-center gap-1 shrink-0">
          <AppStoreIcon className="w-7 h-7 sm:w-8 sm:h-8" />
          <p className="font-display font-bold text-sm sm:text-base leading-none mt-0.5">{step.stats?.appStoreRating}</p>
          <StarRow />
        </div>

        <div className="w-px h-10 bg-surface-2 shrink-0" />

        <div className="flex flex-col items-center gap-1 shrink-0">
          <p className="font-display font-extrabold text-lg sm:text-xl leading-none">{users}</p>
          <p className="text-[10px] sm:text-xs text-ink-muted mt-1">{t('socialProof.downloadsLabel')}</p>
        </div>

        <div className="w-px h-10 bg-surface-2 shrink-0" />

        <div className="flex flex-col items-center gap-1 shrink-0">
          <GooglePlayIcon className="w-7 h-7 sm:w-8 sm:h-8" />
          <p className="font-display font-bold text-sm sm:text-base leading-none mt-0.5">{step.stats?.googlePlayRating}</p>
          <StarRow />
        </div>
      </div>

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={onNext}>{t('socialProof.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GoalSelectStep — estrutura da tela "What's your main fitness goal?" do
// Befit: lista com ícone + destaque ciano na opção marcada + caixa de
// feedback dinâmica (muda conforme a escolha) + botão "Continuar" explícito
// (igual ao das telas anteriores, com o mesmo pulsar). Os 4 valores batem
// com o que o gerador de plano já espera (ver comentário no quizConfig.js).
function GoalSelectStep({ step, value, onAnswer, onNext }) {
  // Pedido da Gladis (01/10/2026): entrar com a 1ª opção já selecionada e o
  // botão "Continuar" já aceso (ciano) desde o início da tela, em vez de
  // começar apagado/escuro e só acender depois do toque.
  const [selected, setSelected] = useState(value ?? step.options[0]?.value ?? null);

  function confirm() {
    onAnswer(selected);
    onNext();
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('goal.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`goal.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => setSelected(opt.value)}
          />
        ))}
      </div>

      {selected && (
        <div className="mt-4 shrink-0 rounded-xl2 border border-cyan/30 bg-surface px-4 py-2.5">
          <p className="text-xs text-ink-muted leading-snug">
            <span className="text-cyan font-semibold">{t('goal.feedbackLead')}</span>{' '}
            {t(`goal.feedback_${selected}`)}
          </p>
        </div>
      )}

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={confirm} disabled={!selected}>
          {t('socialProof.cta')}
        </PrimaryButton>
      </div>
    </div>
  );
}

// BodyGoalStep — estrutura da tela "Which body type are you aiming for?"
// do Befit: lista só ícone + rótulo (mesmo padrão visual de OptionCard já
// usado em 'experience' logo abaixo), seleção única que avança sozinha
// (sem botão Continuar), igual lá. Textos 100% via i18n (bodyGoal.*).
function BodyGoalStep({ step, value, onAnswer, onNext, answers }) {
  const [selected, setSelected] = useState(value ?? null);
  // Pedido da Gladis (01/10/2026): "tem a seção homem e mulher, não pode
  // colocar magro(a) — quando for homem tem que falar diretamente com ele,
  // igual pra mulher". Mesmo padrão de gênero já usado em BodyShapeStep /
  // SocialProofStep: 'male' -> rótulo masculino, 'female' -> feminino,
  // qualquer outra coisa (ou ainda não respondido) -> versão neutra.
  const genderAnswer = answers?.gender;
  const genderSuffix = genderAnswer === 'male' ? 'male' : genderAnswer === 'female' ? 'female' : 'neutral';

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('bodyGoal.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`bodyGoal.option_${opt.value}_${genderSuffix}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// LastBestStep — estrutura da tela "When did you last feel your best
// physically?" do Befit: lista só ícone + rótulo, seleção única que avança
// sozinha (sem botão Continuar), igual 'body_goal'/'experience'. Nenhuma
// das opções tem gênero gramatical, então não precisa de variante
// masculino/feminino aqui (diferente de bodyShape/bodyGoal).
function LastBestStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('lastBest.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`lastBest.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// WeightTrendStep — estrutura da tela "How does your weight typically
// change?" do Befit: lista só ícone + rótulo, avança sozinho (sem botão
// Continuar), igual 'last_felt_best'. Sem gênero gramatical nas opções.
function WeightTrendStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('weightTrend.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`weightTrend.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// TransformPromoStep — tela transitória (sem progress bar) que promete
// mostrar "do corpo atual pro corpo desejado", puxando as respostas reais
// de 'body_shape'/'body_goal' já dadas (gender-aware, igual BodyShapeStep).
// A imagem é PRINT DE VERDADE do app (não foto de banco) — fica como
// placeholder até a Gladis mandar a captura real.
function TransformPromoStep({ step, answers, onNext }) {
  const genderAnswer = answers?.gender;
  const genderSuffix = genderAnswer === 'male' ? 'male' : genderAnswer === 'female' ? 'female' : 'neutral';
  // Pedido da Gladis (01/10/2026): "o texto tem que gerar desejo... ninguém
  // quer sair do 'definido' pro 'magro'" — juntar 'de {atual} pra {meta}'
  // literalmente pode criar combinações que soam como PIORA (ex: definido →
  // magro), já que as duas listas de opções compartilham alguns valores com
  // sentidos diferentes (atual vs. desejado). Pra nunca soar negativo, a
  // frase só fala do objetivo (sempre positivo/aspiracional), sem comparar
  // com o corpo atual.
  const targetBody = answers?.body_goal
    ? t(`bodyGoal.option_${answers.body_goal}_${genderSuffix}`)
    : t(`bodyGoal.option_atletico_${genderSuffix}`);

  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight text-center shrink-0">
        {t('transformPromo.headline', { target: targetBody })}
      </h2>
      <p className="text-ink-muted text-sm text-center mt-3 shrink-0">
        {t('transformPromo.subcopyLead')}{' '}
        <span className="text-cyan font-semibold">{t('transformPromo.subcopyHighlight1')}</span>{' '}
        {t('transformPromo.subcopyMid')}{' '}
        <span className="text-cyan font-semibold">{t('transformPromo.subcopyHighlight2')}</span>.
      </p>

      <ImagePlaceholder
        src={step.appScreenshot}
        label="Print do app FitClutch aqui"
        className={
          step.appScreenshot
            ? 'block mx-auto w-auto max-w-full max-h-[34vh] object-contain mt-4 shrink-0'
            : 'w-full aspect-[9/16] max-h-[42vh] object-cover mt-5 shrink-0'
        }
      />

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={onNext}>{t('transformPromo.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// PushUpsStep — estrutura da tela "What's your max push-ups in one go?"
// do Befit: lista só ícone + rótulo, avança sozinho (sem botão Continuar),
// igual 'weight_trend'. Sem gênero gramatical nas opções.
function PushUpsStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('pushUps.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`pushUps.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// SquatsStep — estrutura da tela "How many squats can you do in a row
// without a break?" do Befit: lista só ícone + rótulo, avança sozinho
// (sem botão Continuar), igual 'push_ups'. Sem gênero gramatical.
function SquatsStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('squats.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`squats.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// FlexibilityStep — estrutura da tela "How flexible are you?" do Befit:
// lista só ícone + rótulo, avança sozinho (sem botão Continuar), igual
// 'squats'. Sem gênero gramatical.
function FlexibilityStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('flexibility.question')}
      </h2>

      <div className="flex flex-col gap-3 mt-5 shrink-0">
        {step.options.map((opt) => (
          <OptionCard
            key={opt.value}
            icon={opt.icon}
            label={t(`flexibility.option_${opt.value}`)}
            selected={selected === opt.value}
            onClick={() => choose(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// ProgressPromoStep — tela transitória (sem progress bar) que promete
// acompanhamento automático de progresso, igual TransformPromoStep. Imagem
// é PRINT DE VERDADE do app (não foto de banco) — placeholder até a Gladis
// mandar a captura real.
function ProgressPromoStep({ step, onNext }) {
  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight text-center shrink-0">
        {t('progressPromo.headline')}
      </h2>
      <p className="text-ink-muted text-sm text-center mt-3 shrink-0">
        {t('progressPromo.subcopyLead')}{' '}
        <span className="text-cyan font-semibold">{t('progressPromo.subcopyHighlight')}</span>
        {' '}{t('progressPromo.subcopyTail')}
      </p>

      <ImagePlaceholder
        src={step.appScreenshot}
        label="Print do app FitClutch aqui"
        className={
          step.appScreenshot
            ? 'block mx-auto w-auto max-w-full max-h-[48vh] object-contain mt-5 shrink-0'
            : 'w-full aspect-[9/16] max-h-[42vh] object-cover mt-5 shrink-0'
        }
      />

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={onNext}>{t('progressPromo.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TrainingSummaryPromoStep({ step, answers, onNext }) {
  const days = t(`trainingSummaryPromo.days_${answers?.days_per_week ?? 4}`);
  const duration = t(`workoutDuration.option_${answers?.workout_duration ?? 'medio'}_badge`);

  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight text-center shrink-0">
        {t('trainingSummaryPromo.headline', { days, duration })}
      </h2>
      <p className="text-ink-muted text-sm text-center mt-3 shrink-0">
        {t('trainingSummaryPromo.subcopyLead')}{' '}
        <span className="text-cyan font-semibold">{t('trainingSummaryPromo.subcopyHighlight')}</span>
        {t('trainingSummaryPromo.subcopyTail')}
      </p>

      <ImagePlaceholder
        src={step.appScreenshot}
        label="Print do app FitClutch aqui"
        className={
          step.appScreenshot
            ? 'block mx-auto w-auto max-w-full max-h-[48vh] object-contain mt-5 shrink-0'
            : 'w-full aspect-[9/16] max-h-[42vh] object-cover mt-5 shrink-0'
        }
      />

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={onNext}>{t('trainingSummaryPromo.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// TargetZoneStep — estrutura da tela "What are your target zones?" do
// Befit: lista de 4 zonas à esquerda + imagem do corpo à direita que
// ACENDE a zona escolhida (imagens geradas pela Gladis no ChatGPT,
// recortadas de uma grade 2x2 + 1 neutra). Gênero-aware igual body_shape.
// CORRIGIDO 01/10/2026 (pedido da Gladis, comparando com o print do
// Befit): (1) NÃO avança sozinho — só toca/realça, precisa do botão
// "Continuar" explícito, igual o exemplo; (2) imagem bem maior, ocupando
// mais espaço, igual o exemplo; (3) as imagens geradas têm fundo PRETO
// sólido (não transparente) — usamos mix-blend-mode: screen pra "sumir"
// com esse preto contra o fundo escuro do app, em vez de aparecer um
// quadro preto por cima.
function TargetZoneStep({ step, value, onAnswer, onNext, answers }) {
  // Pedido da Gladis (01/10/2026): "tem que estar apagado todos os
  // músculos, só o corpo primeiro. Depois a pessoa seleciona" — ao
  // contrário dos outros steps de seleção única, aqui NÃO começa com a
  // 1ª opção já marcada; começa sem nada selecionado (corpo neutro, sem
  // nenhuma zona acesa), só acende quando a pessoa toca.
  const [selected, setSelected] = useState(value ?? null);
  const genderAnswer = answers?.gender;
  const genderImages = step.images?.[genderAnswer] ?? step.images?.male ?? null;
  const activeImage = genderImages ? (genderImages[selected] ?? genderImages.neutral) : null;

  function confirm() {
    onAnswer(selected);
    onNext();
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight shrink-0">
        {t('focusArea.question')}
      </h2>
      <p className="text-ink-muted text-sm mt-1 shrink-0">{t('focusArea.subtitle')}</p>

      <div className="flex gap-2 mt-5 flex-1 min-h-0 items-center">
        <div className="flex flex-col gap-2.5 flex-1 shrink-0 min-w-0">
          {step.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              className={`text-left px-4 py-3.5 rounded-xl2 border-2 font-display font-semibold text-sm transition-colors ${
                selected === opt.value ? 'border-cyan bg-surface-2 text-cyan' : 'border-transparent bg-surface text-ink'
              }`}
            >
              {t(`focusArea.option_${opt.value}`)}
            </button>
          ))}
        </div>

        {/* Imagens com fundo TRANSPARENTE de verdade (pedido da Gladis,
            01/10/2026 — Canva, canvas 800x1200px) substituindo o truque
            de mix-blend-mode usado antes com as imagens de fundo preto. */}
        {activeImage && (
          <img src={activeImage} alt="" className="w-52 shrink-0 aspect-[2/3] object-contain" />
        )}
      </div>

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={confirm} disabled={!selected}>{t('focusArea.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// BodyShapeStep — estrutura da tela "Which of these best matches your
// shape today?" do Befit: grade 2x2 com FOTO + rótulo embaixo (mesmo
// padrão já usado em age_select/gender: placeholder de banco de imagens
// que a Gladis troca depois pela foto definitiva), foto muda por gênero.
// Seleção única que avança sozinha (sem botão Continuar). Resposta
// 'body_shape' guardada pra uso futuro.
function BodyShapeStep({ step, value, onAnswer, onNext, answers }) {
  const [selected, setSelected] = useState(value ?? null);
  const genderAnswer = answers?.gender;
  // Pedido da Gladis (01/10/2026): "Magro(a) não pode existir, faz certo" —
  // mesmo padrão de gênero do BodyGoalStep: fala direto com a pessoa
  // (masculino/feminino), com uma versão neutra só pra quando ainda não
  // respondeu ou escolheu "prefiro não dizer".
  const genderSuffix = genderAnswer === 'male' ? 'male' : genderAnswer === 'female' ? 'female' : 'neutral';

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('bodyShape.question')}
      </h2>

      <div className="grid grid-cols-2 auto-rows-fr gap-3 sm:gap-4 mt-5 flex-1 min-h-0">
        {step.options.map((opt) => {
          const img = opt.images?.[genderAnswer] ?? opt.images?.female ?? null;
          return (
            <button
              key={opt.value}
              onClick={() => choose(opt.value)}
              className={`rounded-xl2 overflow-hidden border-2 transition-colors flex flex-col ${
                selected === opt.value ? 'border-cyan' : 'border-transparent'
              }`}
            >
              <AgeCardImage src={img} label={t(`bodyShape.option_${opt.value}_${genderSuffix}`)} />
              <div className="bg-surface px-2.5 py-2 flex items-center justify-center shrink-0">
                <span className="font-display font-semibold text-xs sm:text-base">
                  {t(`bodyShape.option_${opt.value}_${genderSuffix}`)}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OptionsStep({ step, value, onAnswer, onNext, withImages, gender }) {
  const [selected, setSelected] = useState(value ?? null);
  // Pedido da Gladis (01/10/2026): telas antigas (criadas antes do padrão de
  // i18n por chave `t()`) também precisam ficar no mesmo estilo das novas —
  // "principalmente se for tela antiga". Quando o step traz `i18nKey`, lemos
  // texto via t() em vez do PT fixo salvo no config; sem `i18nKey`, mantém o
  // comportamento antigo (outras telas ainda não convertidas não quebram).
  const k = step.i18nKey;
  const question = k ? t(`${k}.question`) : step.question;
  const subtitle = k ? t(`${k}.subtitle`) : step.subtitle;

  function choose(v) {
    setSelected(v);
    onAnswer(v);
    setTimeout(onNext, 180); // seleção única avança sozinha, com micro-delay pra dar feedback visual
  }

  return (
    <div className="py-2">
      <h2 className="font-display text-2xl font-bold mb-1">{question}</h2>
      {subtitle && <p className="text-ink-muted mb-4">{subtitle}</p>}
      <div className={withImages ? 'grid grid-cols-2 gap-3 mt-4' : 'flex flex-col gap-3 mt-4'}>
        {step.options.map((opt) => {
          const label = k ? t(`${k}.option_${opt.value}`) : opt.label;
          const optSubtitle = k && opt.subtitle ? t(`${k}.option_${opt.value}_subtitle`) : opt.subtitle;
          const badge = k && opt.badge ? t(`${k}.option_${opt.value}_badge`) : opt.badge;
          return withImages ? (
            <button
              key={opt.value}
              onClick={() => choose(opt.value)}
              className={`rounded-xl2 overflow-hidden border ${selected === opt.value ? 'border-cyan' : 'border-transparent'}`}
            >
              <ImagePlaceholder src={resolveImage(opt.image, gender)} className="w-full h-32 object-cover" label={label} />
              <div className="bg-surface p-2 text-sm font-semibold text-left">{label}</div>
            </button>
          ) : (
            <OptionCard
              key={opt.value}
              icon={opt.icon}
              label={label}
              subtitle={optSubtitle}
              badge={badge}
              recommended={opt.recommended}
              selected={selected === opt.value}
              onClick={() => choose(opt.value)}
            />
          );
        })}
      </div>
      {step.footnote && <p className="text-xs text-ink-muted mt-6 text-center">{step.footnote}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
function MultiSelectStep({ step, value, onAnswer, onNext, gender }) {
  const [selected, setSelected] = useState(value ?? null);

  // Pedido da Gladis (01/10/2026): essa tela virou seleção ÚNICA — ao tocar
  // numa área, avança sozinha pra próxima (igual body_shape), sem botão
  // Continuar. Guardamos a resposta como array de 1 item pra não quebrar a
  // lógica do gerador de plano (aiPlanAdvisor/planGenerator esperam
  // `answers.injuries` como array).
  function choose(v) {
    setSelected(v);
    onAnswer([v]);
    setTimeout(onNext, 180);
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0">
        {t('injuries.question')}
      </h2>

      <div className="grid grid-cols-2 auto-rows-fr gap-3 sm:gap-4 mt-5 flex-1 min-h-0">
        {step.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => choose(opt.value)}
            className={`rounded-xl2 overflow-hidden border-2 transition-colors flex flex-col ${
              selected === opt.value ? 'border-cyan' : 'border-transparent'
            }`}
          >
            <AgeCardImage
              src={opt.images?.[gender === 'female' ? 'female' : 'male'] ?? opt.image}
              label={t(`injuries.option_${opt.value}`)}
            />
            <div className="bg-surface px-2.5 py-2 flex items-center justify-center shrink-0">
              <span className="font-display font-semibold text-xs sm:text-base">
                {t(`injuries.option_${opt.value}`)}
              </span>
            </div>
          </button>
        ))}
      </div>

      <p className="text-xs text-ink-muted mt-4 shrink-0 pb-4">{t('injuries.disclaimer')}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grade de "copos" (1 a 8) pra escolher o consumo diário de água — própria
// (ícone de copo desenhado em SVG simples, sem icon-pack pronto pra isso).
// Copos até o selecionado acendem em ciano (contador visual), igual à
// lógica de "nível" das telas de sono/experiência. Botão Continuar explícito
// (não avança sozinho), igual à referência do Befit pra essa tela.
function GlassIcon({ filled }) {
  return (
    <svg width="28" height="34" viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 2H24L21.5 30C21.3 31.6 20 33 18.3 33H9.7C8 33 6.7 31.6 6.5 30L4 2Z"
        stroke={filled ? 'currentColor' : 'currentColor'}
        strokeOpacity={filled ? 1 : 0.4}
        strokeWidth="2"
        fill={filled ? 'currentColor' : 'none'}
        fillOpacity={filled ? 0.15 : 0}
      />
      <line x1="10" y1="10" x2="10" y2="24" stroke="currentColor" strokeOpacity={filled ? 0.9 : 0.4} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function WaterIntakeStep({ step, value, onAnswer, onNext }) {
  const [selected, setSelected] = useState(value ?? null);
  const glasses = Array.from({ length: step.max - step.min + 1 }, (_, i) => step.min + i);

  function confirm() {
    onAnswer(selected);
    onNext();
  }

  return (
    <div className="min-h-full flex flex-col">
      <h2 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight shrink-0 text-center">
        {t('waterIntake.question')}
      </h2>
      <p className="text-ink-muted text-sm text-center mt-2 shrink-0">{t('waterIntake.subtitle')}</p>

      <div className="grid grid-cols-4 gap-3 sm:gap-4 mt-6 shrink-0">
        {glasses.map((n) => {
          const isFilled = selected !== null && n <= selected;
          const isSelected = selected === n;
          return (
            <button
              key={n}
              onClick={() => setSelected(n)}
              className={`relative rounded-xl2 py-3 flex items-center justify-center border transition-colors ${
                isSelected ? 'border-cyan bg-surface-2' : 'border-transparent bg-surface'
              } ${isFilled ? 'text-cyan' : 'text-ink-muted'}`}
            >
              <span className="absolute top-1.5 right-1.5 text-[10px] font-display font-bold bg-surface-2 rounded-full w-6 h-6 flex items-center justify-center">
                x{n}
              </span>
              <GlassIcon filled={isFilled} />
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={confirm} disabled={selected === null}>
          {t('waterIntake.cta')}
        </PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tela de recapitulação pedida pela Gladis (01/10/2026), seguindo a
// estrutura da tela "Aqui está o seu perfil" do Befit: foto + gauge de IMC +
// 4 linhas de classificação + Continuar. Reaproveita a MESMA foto por
// gênero/body_shape já usada no step body_shape (sem imagem nova), e as 4
// classificações vêm de computeProfileSummary (derivadas de respostas já
// dadas, nunca inventadas — ver fitnessScore.js).
function ProfileSummaryStep({ step, answers, gender, onNext }) {
  const summary = useMemo(() => computeProfileSummary(answers), [answers]);
  const bodyShapeStep = useMemo(() => quizSteps.find((s) => s.id === 'body_shape'), []);
  const bodyShapeOpt = bodyShapeStep?.options.find((o) => o.value === answers.body_shape);
  const photo = bodyShapeOpt ? resolveImage(bodyShapeOpt.images, gender) : null;

  const imcLabel = summary.imc != null ? summary.imc.toFixed(1).replace('.', ',') : '—';

  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight text-center shrink-0">
        {t('profileSummary.headline')}
      </h2>

      <ImagePlaceholder
        src={photo}
        label="Foto do perfil"
        className="w-full aspect-[3/4] max-h-[32vh] object-cover rounded-xl2 mt-4 shrink-0"
      />

      <div className="mt-4 shrink-0">
        <div className="relative" style={{ marginLeft: `calc(${summary.imcPct}% - 30px)`, width: '60px' }}>
          <div className="bg-surface-2 rounded-lg px-2 py-1 text-center text-xs font-display font-bold whitespace-nowrap">
            {t('profileSummary.imcLabel', { imc: imcLabel })}
          </div>
        </div>
        <div className="h-2.5 rounded-full mt-1" style={{ background: 'linear-gradient(90deg, #3DD6F2 0%, #3DF291 33%, #F2C53D 66%, #F24F3D 100%)' }} />
        <div
          className="w-3 h-3 rounded-full bg-white border-2 border-bg -mt-[9.5px] shadow"
          style={{ marginLeft: `calc(${summary.imcPct}% - 6px)` }}
        />
        <div className="flex justify-between text-[10px] text-ink-muted font-display font-semibold uppercase tracking-wide mt-2">
          <span>{t('profileSummary.imcAbaixo')}</span>
          <span>{t('profileSummary.imcNormal')}</span>
          <span>{t('profileSummary.imcAcima')}</span>
          <span>{t('profileSummary.imcObeso')}</span>
        </div>
      </div>

      <div className="mt-5 flex flex-col divide-y divide-surface-2 shrink-0">
        <ProfileSummaryRow icon="ti-body" label={t('profileSummary.bodyTypeLabel')} value={t(`profileSummary.bodyType_${summary.bodyType}`)} />
        <ProfileSummaryRow icon="ti-pulse" label={t('profileSummary.lifestyleLabel')} value={t(`profileSummary.lifestyle_${summary.lifestyle}`)} />
        <ProfileSummaryRow icon="ti-barbell" label={t('profileSummary.conditioningLabel')} value={t(`profileSummary.conditioning_${summary.conditioning}`)} />
        <ProfileSummaryRow icon="ti-flame" label={t('profileSummary.metabolismLabel')} value={t(`profileSummary.metabolism_${summary.metabolism}`)} />
      </div>

      {/* Pedido da Gladis (01/10/2026): essa tela tem bastante conteúdo
          (foto + gauge + 4 linhas) e o botão ficava escondido lá embaixo
          sem a pessoa perceber que precisava rolar. Botão flutuante
          (sticky no rodapé do phone-frame, que é quem rola) só nessa
          tela — não mexe no comportamento padrão (botão no fim do fluxo)
          das outras telas do funil. */}
      <div className="sticky bottom-0 -mx-5 px-5 pt-3 pb-4 mt-5 bg-gradient-to-t from-bg via-bg to-transparent shrink-0">
        <PrimaryButton onClick={onNext}>{t('profileSummary.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

function ProfileSummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="w-9 h-9 shrink-0 rounded-full bg-surface-2 flex items-center justify-center text-cyan">
        <IconGlyph name={icon} size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-ink-muted">{label}</div>
        <div className="font-display font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gráfico de duas curvas (com FitClutch x sem FitClutch) — própria versão
// em SVG desenhado à mão (sem lib de gráfico pra uma tela só), paleta ciano
// em vez do verde do Befit, texto 100% próprio nos 8 idiomas.
function ConsistencyChartStep({ answers, onNext }) {
  // Converte os 3 percentuais (0-100, calculados de verdade a partir das
  // respostas em computeConsistencyProjection) em coordenadas Y do SVG:
  // 100% -> topo do gráfico (y=25), 0% -> base (y=185).
  const { startPct, withPct, withoutPct } = useMemo(() => computeConsistencyProjection(answers), [answers]);
  const toY = (pct) => 185 - (pct / 100) * 160;
  const yStart = toY(startPct);
  const yWith = toY(withPct);
  const yWithout = toY(withoutPct);
  const midWith = (yStart + yWith) / 2 - 20;
  const midWithout = (yStart + yWithout) / 2 + 15;
  const pathWith = `M20,${yStart} C150,${midWith} 280,${midWith} 380,${yWith}`;
  const pathWithout = `M20,${yStart} C150,${midWithout} 250,${midWithout} 380,${yWithout}`;

  // Pedido da Gladis (01/10/2026, 2ª rodada): "a barra não sobe desde o
  // início... ele está todo completo aí só se mexe". Animar só o ponto
  // final (Y) não bastava — a linha inteira já aparecia desenhada na tela.
  // Agora as duas linhas DESENHAM de verdade da esquerda pra direita
  // (stroke-dashoffset sobre o comprimento real do path, via
  // getTotalLength), como um gráfico crescendo de fato desde o começo.
  const withRef = useRef(null);
  const withoutRef = useRef(null);
  const [drawn, setDrawn] = useState(0); // 0 a 1, progresso do desenho
  const [lengths, setLengths] = useState({ with: 1, without: 1 });

  useEffect(() => {
    if (withRef.current && withoutRef.current) {
      setLengths({
        with: withRef.current.getTotalLength(),
        without: withoutRef.current.getTotalLength(),
      });
    }
  }, [pathWith, pathWithout]);

  useEffect(() => {
    setDrawn(0);
    let raf;
    const start = performance.now();
    const duration = 1200;
    function tick(now) {
      const p = Math.min(1, (now - start) / duration);
      setDrawn(1 - Math.pow(1 - p, 3)); // ease-out cúbico
      if (p < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pathWith, pathWithout]);

  // Números dos pills contam junto com o desenho da linha
  const animWithPct = startPct + (withPct - startPct) * drawn;
  const animWithoutPct = startPct + (withoutPct - startPct) * drawn;

  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight text-center shrink-0">
        {t('consistencyChart.headline')}
      </h2>
      <p className="text-ink-muted text-sm text-center mt-3 shrink-0">
        {t('consistencyChart.subcopyLead')}{' '}
        <span className="text-cyan font-semibold">{t('consistencyChart.subcopyHighlight1')}</span>{' '}
        {t('consistencyChart.subcopyMid')}{' '}
        <span className="text-cyan font-semibold">{t('consistencyChart.subcopyHighlight2')}</span>.
      </p>

      <div className="bg-surface rounded-xl2 p-5 mt-5 shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">{t('consistencyChart.chartTitle')}</h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan shrink-0" />
              <span className="text-[11px] font-semibold text-cyan">{t('consistencyChart.withLabel')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-ink-muted shrink-0" />
              <span className="text-[11px] font-semibold text-ink-muted">{t('consistencyChart.withoutLabel')}</span>
            </div>
          </div>
        </div>
        <svg viewBox="0 0 400 210" className="w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="consistencyFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#19D7F2" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#19D7F2" stopOpacity="0" />
            </linearGradient>
            <clipPath id="consistencyFillClip">
              <rect x="0" y="0" width={380 * drawn} height="210" />
            </clipPath>
          </defs>
          <path d={`${pathWith} L380,210 L20,210 Z`} fill="url(#consistencyFill)" clipPath="url(#consistencyFillClip)" />
          <path
            ref={withoutRef}
            d={pathWithout}
            stroke="#5B6672"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={lengths.without}
            strokeDashoffset={lengths.without * (1 - drawn)}
          />
          <path
            ref={withRef}
            d={pathWith}
            stroke="#19D7F2"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={lengths.with}
            strokeDashoffset={lengths.with * (1 - drawn)}
          />
          <circle cx="20" cy={yStart} r="5" fill="#fff" />
          <circle cx="380" cy={yWithout} r="5" fill="#5B6672" opacity={drawn > 0.95 ? 1 : 0} />
          <circle cx="380" cy={yWith} r="5" fill="#19D7F2" opacity={drawn > 0.95 ? 1 : 0} />

          {/* Pedido da Gladis (01/10/2026): números reais em cada ponto,
              pra deixar visível que o gráfico é calculado (não decoração) —
              mesmo padrão de pill do profile_summary (IMC - XX,X). */}
          <g transform={`translate(20, ${yStart - 16})`}>
            <rect x="-22" y="-14" width="44" height="22" rx="8" fill="#20262E" />
            <text x="0" y="2" fontSize="12" fontWeight="700" fill="#fff" textAnchor="middle" fontFamily="inherit">
              {Math.round(startPct)}
            </text>
          </g>
          <g transform={`translate(380, ${yWith - 16})`} opacity={drawn > 0.8 ? 1 : 0}>
            <rect x="-26" y="-14" width="52" height="22" rx="8" fill="#19D7F2" />
            <text x="0" y="2" fontSize="12" fontWeight="700" fill="#06252B" textAnchor="middle" fontFamily="inherit">
              {Math.round(animWithPct)}
            </text>
          </g>
          <g transform={`translate(380, ${yWithout + 20})`} opacity={drawn > 0.8 ? 1 : 0}>
            <rect x="-26" y="-14" width="52" height="22" rx="8" fill="#20262E" stroke="#5B6672" strokeWidth="1" />
            <text x="0" y="2" fontSize="12" fontWeight="700" fill="#C7CDD4" textAnchor="middle" fontFamily="inherit">
              {Math.round(animWithoutPct)}
            </text>
          </g>

          <text x="20" y="195" fontSize="12" fill="#8A95A1" fontFamily="inherit">{t('consistencyChart.nowLabel')}</text>
        </svg>
      </div>

      <p className="text-xs text-ink-muted italic text-center mt-4 shrink-0">{t('consistencyChart.disclaimer')}</p>

      <div className="flex-1 min-h-0" />

      <div className="cta-wrap-gap shrink-0 pb-4">
        <PrimaryButton onClick={onNext}>{t('consistencyChart.cta')}</PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tela de loading própria (headline + % animado 0→100 + checklist +
// depoimento), seguindo a estrutura da referência do Befit ("Creating your
// Personal Workout Plan"), mas com texto/depoimento próprios. Avança
// sozinha quando a animação termina (sem botão).
function ProgressLoaderStep({ step, onNext }) {
  // Duração um pouco maior que o original (3.4s) pra dar tempo da pessoa
  // ver o carrossel de depoimentos passar pelo menos uma vez — pedido da
  // Gladis (01/10/2026): "a parte de depoimentos quero que vai passando
  // para o lado com mais depoimentos... pelo menos uns 4".
  const duration = step.duration ?? 6200;
  const [pct, setPct] = useState(0);
  const checklistKeys = ['bodyType', 'fitnessLevel', 'plan'];
  const activeIndex = Math.min(checklistKeys.length - 1, Math.floor((pct / 100) * checklistKeys.length));
  const testimonialKeys = ['testimonial1', 'testimonial2', 'testimonial3', 'testimonial4'];
  const [testiIndex, setTestiIndex] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    function tick(now) {
      const p = Math.min(1, (now - start) / duration);
      setPct(Math.round(p * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(onNext, 500);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Troca o depoimento em destaque sozinho, num intervalo fixo, independente
  // da duração total da tela — assim sempre dá pra ver mais de um passar.
  useEffect(() => {
    const id = setInterval(() => {
      setTestiIndex((i) => (i + 1) % testimonialKeys.length);
    }, 1500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight">
        {t('progressLoader.headline')}
      </h2>

      <div className="font-display font-extrabold text-6xl text-cyan mt-8">{pct}%</div>

      <div className="bg-surface rounded-xl2 p-5 mt-8 w-full max-w-xs flex flex-col divide-y divide-surface-2">
        {checklistKeys.map((key, i) => (
          <div key={key} className={`flex items-center justify-between gap-2 py-3 text-sm text-left ${i <= activeIndex ? 'text-ink' : 'text-ink-muted/40'}`}>
            <span>{t(`progressLoader.item_${key}`)}</span>
            {i < activeIndex ? (
              <div className="w-[18px] h-[18px] rounded-full bg-cyan flex items-center justify-center shrink-0">
                <IconGlyph name="ti-check" size={11} className="text-bg" />
              </div>
            ) : i === activeIndex ? (
              <div className="w-4 h-4 rounded-full border-2 border-cyan border-t-transparent animate-spin shrink-0" />
            ) : (
              <IconGlyph name="ti-circle-dashed" size={18} />
            )}
          </div>
        ))}
      </div>

      {/* Todos os cards ocupam a MESMA célula do grid, então a altura do
          carrossel é a do maior depoimento — nada fica cortado, em
          qualquer idioma ou tamanho de texto. */}
      <div className="grid mt-6 w-full max-w-xs overflow-hidden">
        {testimonialKeys.map((key, i) => (
          <div
            key={key}
            className="[grid-area:1/1] bg-surface rounded-xl2 p-4 text-left transition-all duration-500 ease-out"
            style={{
              transform: `translateX(${(i - testiIndex) * 100}%)`,
              opacity: i === testiIndex ? 1 : 0,
            }}
          >
            <div className="font-display font-bold text-sm">{t(`progressLoader.${key}_title`)}</div>
            <div className="flex gap-0.5 mt-1 text-cyan">
              {[0, 1, 2, 3, 4].map((s) => (
                <IconGlyph key={s} name="ti-star" size={14} />
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-2">{t(`progressLoader.${key}_body`)}</p>
            <p className="text-xs font-semibold mt-2">{t(`progressLoader.${key}_author`)}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-3">
        {testimonialKeys.map((key, i) => (
          <div key={key} className={`h-1.5 rounded-full transition-all ${i === testiIndex ? 'w-4 bg-cyan' : 'w-1.5 bg-surface-2'}`} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Captura de e-mail (lead), sem barra de progresso — igual à referência do
// Befit. Guarda a resposta como string simples (answers.email_capture).
function EmailCaptureStep({ value, answers, onAnswer, onNext }) {
  const [email, setEmail] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function confirm() {
    if (!isValid || saving) return;
    setSaving(true);
    onAnswer(email);
    // Grava o lead no Supabase (tabela public.leads) pra já existir um
    // contato salvo mesmo que a pessoa abandone o funil antes de criar
    // conta — é a base pra email de recuperação/remarketing. Nunca trava
    // o avanço da tela em caso de falha (ver lib/leads.js).
    await saveLead(email, answers);
    onNext();
  }

  return (
    <div className="min-h-full flex flex-col py-2">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight shrink-0">
        {t('emailCapture.headlineLead')}{' '}
        <span className="text-cyan">{t('emailCapture.headlineHighlight')}</span>
      </h2>

      <input
        type="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('emailCapture.placeholder')}
        className="w-full mt-5 px-4 py-4 rounded-full bg-surface text-ink placeholder:text-ink-muted border border-transparent focus:border-cyan outline-none shrink-0"
      />

      <div className="mt-4 shrink-0">
        <PrimaryButton onClick={confirm} disabled={!isValid || saving}>{t('emailCapture.cta')}</PrimaryButton>
      </div>

      <p className="text-xs text-ink-muted mt-4 shrink-0">
        🔒 {t('emailCapture.privacyLead')}{' '}
        <a href="/privacy" className="underline text-cyan">{t('emailCapture.privacyLink')}</a>.
      </p>

      <div className="flex-1 min-h-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cartão-surpresa de desconto: toque pra revelar, confete ao abrir, avança
// sozinho depois de alguns segundos. Só aparece no mercado BR (pt) — é o
// único onde o cupom de boas-vindas de 50% existe de verdade no Stripe hoje
// (ver comentário em quizConfig.js); nos outros idiomas pula direto, nunca
// mostrando um desconto que o checkout daquele mercado não aplicaria.
function DiscountRevealStep({ onNext }) {
  const [revealed, setRevealed] = useState(false);
  const [seconds, setSeconds] = useState(2);
  const [confetti, setConfetti] = useState([]);
  const skip = currentLanguage !== 'pt';

  useEffect(() => {
    if (skip) {
      onNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  function reveal() {
    if (revealed) return;
    setRevealed(true);
    // Confete gerado uma vez, com posição/cor/atraso/rotação aleatórios —
    // puramente decorativo, em CSS (sem dependência nova no bundle).
    const colors = ['#19D7F2', '#ffffff', '#7CE8FA', '#0EA5C4'];
    setConfetti(
      Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.4,
        duration: 2.2 + Math.random() * 1.2,
        size: 6 + Math.random() * 6,
        color: colors[i % colors.length],
        rotate: Math.random() * 360,
      }))
    );
  }

  useEffect(() => {
    if (!revealed) return;
    const id = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(id);
          setTimeout(onNext, 300);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  if (skip) return null;

  return (
    <div className="relative flex-1 flex flex-col items-center text-center py-6 overflow-hidden">
      {confetti.map((c) => (
        <div
          key={c.id}
          className="absolute top-0 rounded-sm pointer-events-none"
          style={{
            left: `${c.left}%`,
            width: c.size,
            height: c.size * 0.4,
            backgroundColor: c.color,
            animation: `fc-confetti-fall ${c.duration}s ease-in ${c.delay}s forwards`,
            transform: `rotate(${c.rotate}deg)`,
          }}
        />
      ))}

      <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight">
        {t('discountReveal.headline')}
      </h2>
      <p className="text-sm text-ink-muted mt-2 max-w-xs">{t('discountReveal.subtitle')}</p>

      <button
        onClick={reveal}
        className="relative mt-8 w-full max-w-xs rounded-xl2 bg-surface border border-surface-2 overflow-hidden"
        style={{ minHeight: 260 }}
      >
        {!revealed ? (
          <div className="flex flex-col items-center justify-center h-full py-12 gap-4">
            <div className="w-16 h-16 rounded-xl2 bg-cyan/15 flex items-center justify-center">
              <IconGlyph name="ti-sparkles" size={30} className="text-cyan" />
            </div>
            <div className="font-display font-bold text-lg">{t('discountReveal.cardTitle')}</div>
            <div className="border-t border-dashed border-surface-2 w-full" />
            <div className="text-ink-muted text-sm">{t('discountReveal.tapToReveal')}</div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-8 gap-1 animate-[fc-reveal-pop_0.35s_ease-out]">
            <div className="font-display font-extrabold text-5xl text-cyan">{t('discountReveal.badgeValue')}</div>
            <div className="text-sm">{t('discountReveal.badgeSubtitle')}</div>
            <div className="border-t border-dashed border-surface-2 w-full my-4" />
            <div className="bg-cyan/10 rounded-xl px-4 py-2 text-center">
              <div className="text-xs text-ink-muted">{t('discountReveal.codeLabel')}</div>
              <div className="font-display font-bold text-cyan tracking-wide">{t('discountReveal.codeValue')}</div>
            </div>
          </div>
        )}
      </button>

      {revealed && (
        <>
          <p className="text-xs text-ink-muted mt-4">{t('discountReveal.appliedNote')}</p>
          <p className="text-xs text-cyan font-semibold mt-1">{t('discountReveal.redirecting', { seconds })}</p>
        </>
      )}

      <style>{`
        @keyframes fc-confetti-fall {
          from { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          to { transform: translateY(420px) rotate(360deg); opacity: 0; }
        }
        @keyframes fc-reveal-pop {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
function BodyMapStep({ step, value, onAnswer, onNext, gender }) {
  const [selected, setSelected] = useState(value ?? null);
  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-4">{step.question}</h2>
      <ImagePlaceholder src={resolveImage(step.bodyImage, gender)} className="w-full h-56 object-contain mb-4" label="Silhueta do corpo" />
      <div className="flex flex-col gap-2">
        {step.options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setSelected(opt.value)}
            className={`flex items-center justify-between px-4 py-3 rounded-xl2 border ${
              selected === opt.value ? 'border-cyan bg-surface-2' : 'border-transparent bg-surface'
            }`}
          >
            <span className="font-medium">{opt.label}</span>
            {opt.tag && <span className="text-xs text-accent">⚠ {opt.tag}</span>}
          </button>
        ))}
      </div>
      <div className="flex-1" />
      <div className="py-4">
        <PrimaryButton
          disabled={!selected}
          onClick={() => {
            onAnswer(selected);
            onNext();
          }}
        >
          Continuar
        </PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function WheelPickerStep({ step, value, onAnswer, onNext }) {
  const [val, setVal] = useState(value ?? step.default);
  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-8 text-center">{step.question}</h2>
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="flex items-center gap-6">
          <button
            className="w-11 h-11 rounded-full bg-surface-2 text-2xl"
            onClick={() => setVal((v) => Math.max(step.min, v - 1))}
          >
            −
          </button>
          <div className="text-center">
            <div className="font-display text-6xl font-extrabold text-cyan">{val}</div>
            <div className="text-ink-muted">{step.unit}</div>
          </div>
          <button
            className="w-11 h-11 rounded-full bg-surface-2 text-2xl"
            onClick={() => setVal((v) => Math.min(step.max, v + 1))}
          >
            +
          </button>
        </div>
        <input
          type="range"
          min={step.min}
          max={step.max}
          value={val}
          onChange={(e) => setVal(Number(e.target.value))}
          className="w-full mt-8 accent-cyan"
        />
      </div>
      <div className="py-4">
        <PrimaryButton
          onClick={() => {
            onAnswer(val);
            onNext();
          }}
        >
          Continuar
        </PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RulerSliderStep({ step, value, onAnswer, onNext }) {
  const [unit, setUnit] = useState(step.defaultUnit);
  const [val, setVal] = useState(value ?? step.default);
  const displayValue = unit === 'ft/in' && step.units.includes('ft/in') ? cmToFtIn(val) : `${val} ${unit}`;

  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-2 text-center">{step.question}</h2>
      <UnitToggle units={step.units} unit={unit} setUnit={setUnit} />
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="font-display text-5xl font-extrabold text-cyan mb-6">{displayValue}</div>
        <StepperSlider min={step.min} max={step.max} value={val} onChange={setVal} />
      </div>
      <div className="py-4">
        <PrimaryButton
          onClick={() => {
            onAnswer(val);
            onNext();
          }}
        >
          Continuar
        </PrimaryButton>
      </div>
    </div>
  );
}

function UnitToggle({ units, unit, setUnit }) {
  if (!units || units.length < 2) return null;
  return (
    <div className="flex justify-center gap-2 mb-4">
      {units.map((u) => (
        <button
          key={u}
          onClick={() => setUnit(u)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
            unit === u ? 'bg-cyan text-bg' : 'bg-surface-2 text-ink-muted'
          }`}
        >
          {u}
        </button>
      ))}
    </div>
  );
}

// Pedido da Gladis (01/10/2026): a barrinha sozinha (input range) ficava
// "pesada"/travando — às vezes passava do número certo pra mais ou pra
// menos. Adicionamos botões -/+ pra ajuste fino de 1 em 1, sem precisar
// arrastar. Usado em altura e peso (os dois rulers do funil).
function StepperSlider({ min, max, value, onChange, step = 1 }) {
  function clamp(n) {
    return Math.min(max, Math.max(min, n));
  }
  return (
    <div className="w-full flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(clamp(value - step))}
        className="shrink-0 w-11 h-11 rounded-full bg-surface-2 text-cyan font-display font-bold text-xl flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Diminuir"
      >
        −
      </button>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 accent-cyan"
      />
      <button
        type="button"
        onClick={() => onChange(clamp(value + step))}
        className="shrink-0 w-11 h-11 rounded-full bg-surface-2 text-cyan font-display font-bold text-xl flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Aumentar"
      >
        +
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RulerSliderFeedbackStep({ step, value, answers, onAnswer, onNext }) {
  const [unit, setUnit] = useState(step.defaultUnit);
  const [val, setVal] = useState(value ?? step.default ?? answers.weight_current ?? 60);
  const displayValue = unit === 'lb' ? `${kgToLb(val)} lb` : `${val} kg`;

  // TELA "weight_current": mostra IMC real calculado com a altura já respondida
  const imc = step.feedback ? computeIMC(val, answers.height) : null;

  // TELA "weight_goal": feedback muda conforme comparação com weight_current
  let feedback = null;
  if (step.feedbackOptions) {
    const current = answers.weight_current ?? val;
    const delta = Math.abs(Math.round(val - current));
    if (val === current) feedback = step.feedbackOptions.maintain;
    else if (val < current) feedback = { ...step.feedbackOptions.lose, title: fillTemplate(step.feedbackOptions.lose.title, { delta }) };
    else feedback = { ...step.feedbackOptions.gain, title: fillTemplate(step.feedbackOptions.gain.title, { delta }) };
  }

  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-2 text-center">{step.question}</h2>
      <UnitToggle units={step.units} unit={unit} setUnit={setUnit} />
      <div className="flex flex-col items-center justify-center py-4">
        <div className="font-display text-5xl font-extrabold text-cyan mb-6">{displayValue}</div>
        <StepperSlider min={step.min} max={step.max} value={val} onChange={setVal} />
      </div>

      {step.feedback && imc != null && (
        <div className="bg-surface-2 rounded-xl2 p-4 mt-4 flex gap-3 items-start">
          <span className="text-2xl">{step.feedback.icon}</span>
          <p className="text-sm text-ink">{fillTemplate(step.feedback.template, { imc })}</p>
        </div>
      )}
      {feedback && (
        <div className="bg-surface-2 rounded-xl2 p-4 mt-4">
          <div className="flex gap-3 items-start">
            <span className="text-2xl">{feedback.icon}</span>
            <div>
              <p className="font-semibold">{feedback.title}</p>
              <p className="text-sm text-ink-muted mt-1">{feedback.body}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1" />
      <div className="py-4 flex flex-col gap-2">
        <PrimaryButton
          onClick={() => {
            onAnswer(val);
            onNext();
          }}
        >
          Continuar
        </PrimaryButton>
        {step.skippable && (
          <button className="text-ink-muted text-sm" onClick={onNext}>
            Pular esta etapa
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PredictionChartStep({ step, answers, onNext }) {
  const peso = answers.weight_goal ?? answers.weight_current ?? '--';
  const data = formatDatePlusDays(90);
  const title = fillTemplate(step.titleTemplate, { peso, data });

  const fromWeight = answers.weight_current ?? 70;
  const toWeight = answers.weight_goal ?? fromWeight;
  const points = useMemo(() => {
    const steps = 6;
    return Array.from({ length: steps + 1 }, (_, i) => {
      const t = i / steps;
      const w = fromWeight + (toWeight - fromWeight) * t;
      const x = 10 + t * 280;
      const y = 90 - ((w - Math.min(fromWeight, toWeight)) / (Math.abs(toWeight - fromWeight) || 1)) * 60;
      return `${x},${Number.isFinite(y) ? y : 50}`;
    }).join(' ');
  }, [fromWeight, toWeight]);

  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-6 text-center leading-snug">{title}</h2>
      <svg viewBox="0 0 300 110" className="w-full h-40">
        <polyline points={points} fill="none" stroke="var(--fc-cyan)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="10" cy="90" r="4" fill="var(--fc-cyan-dark)" />
        <circle cx="290" cy="30" r="5" fill="var(--fc-cyan)" />
      </svg>
      <div className="flex justify-between text-xs text-ink-muted px-1">
        <span>{step.chart.fromLabel}</span>
        <span>{data}</span>
      </div>
      {step.feedback && (
        <div className="bg-surface-2 rounded-xl2 p-4 mt-6 flex gap-3 items-start">
          <span className="text-2xl">{step.feedback.icon}</span>
          <p className="text-sm text-ink">{step.feedback.body}</p>
        </div>
      )}
      <div className="flex-1" />
      <div className="py-4">
        <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TransformationStep({ step, gender, onNext }) {
  return (
    <div className="flex-1 flex flex-col -mx-5">
      <h2 className="font-display text-2xl font-extrabold px-5 mb-1">{step.headline}</h2>
      {step.subtitle && <p className="text-ink-muted px-5 mb-4">{step.subtitle}</p>}
      <div className="flex-1 grid grid-cols-2 gap-1 px-5">
        <TransformationCard
          label={step.beforeLabel}
          points={step.beforePoints}
          image={resolveImage(step.backgroundImageBefore, gender)}
          positive={false}
        />
        <TransformationCard
          label={step.afterLabel}
          points={step.afterPoints}
          image={resolveImage(step.backgroundImageAfter, gender)}
          positive
        />
      </div>
      <div className="px-5 py-6">
        <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
      </div>
    </div>
  );
}

function TransformationCard({ label, points, image, positive }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-xl2 overflow-hidden h-40">
        <ImagePlaceholder src={image} className="w-full h-full object-cover" label={label ?? ''} />
      </div>
      {label && (
        <span className={`text-xs font-bold uppercase tracking-wide ${positive ? 'text-cyan' : 'text-ink-muted'}`}>
          {label}
        </span>
      )}
      {points?.length > 0 && (
        <ul className="flex flex-col gap-1">
          {points.map((p) => (
            <li key={p} className={`text-xs flex items-start gap-1 ${positive ? 'text-ink' : 'text-ink-muted'}`}>
              <span>{positive ? '✓' : '•'}</span> {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ValueComparisonStep({ step, onNext }) {
  const { without, with: withBar } = step.comparison;
  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-1">{step.question}</h2>
      <p className="text-ink-muted mb-8">{step.subtitle}</p>
      <div className="flex items-end justify-center gap-8 h-48">
        <AnimatedBar heightPercent={without.heightPercent} label={without.label} brandLabel={without.brandLabel} tone="muted" />
        <AnimatedBar heightPercent={withBar.heightPercent} label={withBar.label} brandLabel={withBar.brandLabel} tone="cyan" />
      </div>
      {step.footnote && <p className="text-center text-xs text-ink-muted mt-8 uppercase tracking-wide">{step.footnote}</p>}
      {step.bullets?.length > 0 && (
        <div className="flex flex-col gap-3 mt-8">
          {step.bullets.map((b) => (
            <div key={b} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-surface flex items-center justify-center text-cyan text-xs shrink-0">✓</span>
              <span className="text-sm">{b}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex-1" />
      <div className="py-4">
        <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
      </div>
    </div>
  );
}

function AnimatedBar({ heightPercent, label, brandLabel, tone }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGrown(true), 50);
    return () => clearTimeout(t);
  }, []);
  const isCyan = tone === 'cyan';
  return (
    <div className="flex flex-col items-center gap-2 w-24">
      {brandLabel && (
        <span className={`text-[10px] font-extrabold uppercase tracking-wide text-center ${isCyan ? 'text-cyan' : 'text-ink-muted'}`}>
          {brandLabel}
        </span>
      )}
      <span className={`text-lg font-extrabold ${isCyan ? 'text-cyan' : 'text-ink-muted'}`}>{grown ? heightPercent : 0}%</span>
      <div className="w-16 h-40 flex items-end">
        <div
          className={`w-full rounded-t-lg transition-all duration-700 ease-out ${isCyan ? 'bg-cyan-gradient shadow-glow' : 'bg-surface-2'}`}
          style={{ height: grown ? `${heightPercent}%` : '0%' }}
        />
      </div>
      <span className={`text-xs text-center ${isCyan ? 'text-cyan font-semibold' : 'text-ink-muted'}`}>{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PermissionStep({ step, answers, onAnswer, onNext }) {
  const notif = step.previewNotification;
  return (
    <div className="py-2 flex flex-col flex-1">
      <h2 className="font-display text-2xl font-bold mb-1">{step.question}</h2>
      {step.subtitle && <p className="text-ink-muted mb-6">{step.subtitle}</p>}

      {step.tagsLeft && (
        <div className="flex justify-center gap-6 my-8">
          <div className="flex flex-col gap-2 items-end">
            {step.tagsLeft.map((t) => (
              <span key={t} className="bg-surface-2 px-3 py-1 rounded-full text-xs">{t}</span>
            ))}
          </div>
          <div className="w-px bg-surface-2" />
          <div className="flex flex-col gap-2 items-start">
            {step.tagsRight.map((t) => (
              <span key={t} className="bg-cyan/15 text-cyan px-3 py-1 rounded-full text-xs">{t}</span>
            ))}
          </div>
        </div>
      )}

      {notif && (
        <div className="bg-surface-2 rounded-xl2 p-4 my-8 flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-gradient flex items-center justify-center text-bg font-bold">FC</div>
          <div className="flex-1">
            <div className="flex justify-between text-sm">
              <span className="font-semibold">{notif.appName}</span>
              <span className="text-ink-muted text-xs">{notif.timeLabel}</span>
            </div>
            <p className="text-sm text-ink-muted mt-1">
              {fillTemplate(notif.body, { exercicios: 'Supino reto, Remada, Elevação lateral...' })}
            </p>
          </div>
        </div>
      )}

      {step.statProof && <p className="text-sm text-ink-muted text-center mb-4">{step.statProof}</p>}

      <div className="flex-1" />
      <div className="py-4 flex flex-col gap-2">
        <PrimaryButton
          onClick={() => {
            onAnswer(true);
            onNext();
          }}
        >
          {step.primaryAction}
        </PrimaryButton>
        <SecondaryButton
          onClick={() => {
            onAnswer(false);
            onNext();
          }}
        >
          {step.secondaryAction}
        </SecondaryButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function LoadingAnalysisStep({ step, onNext }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const perStep = step.duration / step.steps.length;
    const interval = setInterval(() => {
      setActiveIndex((i) => Math.min(i + 1, step.steps.length - 1));
    }, perStep);
    const timeout = setTimeout(onNext, step.duration);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center py-10">
      <div className="w-16 h-16 rounded-full border-4 border-surface-2 border-t-cyan animate-spin mb-8" />
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {step.steps.map((label, i) => (
          <div key={label} className={`flex items-center gap-2 text-sm ${i <= activeIndex ? 'text-ink' : 'text-ink-muted/40'}`}>
            <span>{i < activeIndex ? '✅' : i === activeIndex ? '⏳' : '○'}</span>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}
